import React from "react";

import type { PaymentElementProps } from "@stripe/react-stripe-js";

import Typography from "@mui/material/Typography";

import PaymentForm from "@app/analytics/account/Billing/checkout/BillingPurchaseForm/PaymentForm";
import {
  BillingDetailsLoading,
  EditFormButton,
  CompletedPaymentInfo,
  StripeElementContainer,
} from "@app/analytics/account/Billing/checkout/styles";
import StripeForm from "@app/analytics/account/Billing/components/StripeForm";
import type { PaymentFormElementOptions } from "@app/analytics/account/Billing/types";
import * as BillingUtils from "@app/analytics/account/Billing/utils";
import ROUTES from "@app/common/routes";
import type { CheckoutBillingInfo, PaymentMethod } from "@app/common/types";
import { withQueryParams } from "@app/common/utils";
import { getRelativeUrl } from "@app/utils/utils";

import { Link } from "@rui/Link";
import Stack from "@rui/Stack";

interface PaymentModuleProps {
  loading?: boolean;
  billingInfo: CheckoutBillingInfo;
  paymentMethod?: PaymentMethod;
  paymentInfoElementOptions?: PaymentFormElementOptions | null;
  onLoadError: PaymentElementProps["onLoadError"];
  onSubmitSettled: (error?: string) => void;
  paymentConfirmReturnUrl: string;
}

const PaymentModule = ({
  loading,
  billingInfo,
  paymentMethod,
  paymentInfoElementOptions,
  paymentConfirmReturnUrl,
  onLoadError,
  onSubmitSettled,
}: PaymentModuleProps) => {
  if (loading) return <BillingDetailsLoading />;

  if (!billingInfo) return null;

  const hidePaymentForm = Boolean(paymentMethod);

  return (
    <>
      {paymentMethod && (
        <CompletedPaymentInfo>
          <Stack>
            <Typography variant="bodySecondaryReg">
              {BillingUtils.getPaymentMethodDescription(paymentMethod)}
            </Typography>
          </Stack>
          <Link
            to={withQueryParams(ROUTES.BILLING_UPDATE, {
              returnTo: getRelativeUrl(window.location.href),
            })}
          >
            <EditFormButton>Edit</EditFormButton>
          </Link>
        </CompletedPaymentInfo>
      )}
      <StripeElementContainer
        in={Boolean(paymentInfoElementOptions) && !hidePaymentForm}
      >
        <StripeForm
          options={{
            clientSecret: paymentInfoElementOptions?.clientSecret,
          }}
          requireClientSecret
        >
          <PaymentForm
            isHidden={hidePaymentForm}
            billingInfo={billingInfo}
            clientSecret={paymentInfoElementOptions?.clientSecret}
            onSubmitSettled={onSubmitSettled}
            paymentConfirmReturnUrl={paymentConfirmReturnUrl}
            type={paymentInfoElementOptions?.type}
            onLoadError={onLoadError}
          />
        </StripeForm>
      </StripeElementContainer>
    </>
  );
};

export default PaymentModule;
